import { DateRange } from 'moment-range';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CalendarIcon } from '../../assets';
import { FilterButton } from '../../overwrites/components/DateFilterButton/FilterButton.styles';
import { Modal } from '../';
import { ModalProps } from '../Modal/Modal';
import { ButtonText, StyledBadge, StyledDateRangePicker } from './DateFilterButton.styles';

interface DateFilterButtonProps {
  modalTitle?: string;
  dateRange?: DateRange;
  maximumDate?: Date;
  minDate?: Date;
  maxDaysCount?: number;
  singleDateSelection?: boolean;
  breakpointShowButtonText?: string;
  onRangeSelect?: (range: DateRange) => void;
  onReset?: () => void;
  disabled?: boolean;
  placeholder?: string;
  rollingMaxDays?: boolean;
}

interface DateRangeModalProps extends Omit<ModalProps, 'children' | 'open' | 'onClose'> {
  selectedRange?: DateRange;
  onRangeSubmit: (range: DateRange) => void;
  modalTitle?: string;
  maximumDate?: Date;
  minDate?: Date;
  maxDaysCount?: number;
  singleDateSelection?: boolean;
  onReset?: () => void;
  onClose?: () => void;
  rollingMaxDays?: boolean;
  t: (key: string, options?: any) => string;
}

const DateRangeModal = React.memo(
  forwardRef(({ selectedRange, onRangeSubmit, singleDateSelection = false, ...rest }: DateRangeModalProps, ref) => {
    const [isOpen, setOpen] = useState(false);
    const [disabledSubmitButton, setDisabledSubmitButton] = useState(false);
    const initialRangeRef = useRef(selectedRange);
    const [localDateRange, setLocalDateRange] = useState<DateRange | undefined>(selectedRange);

    useImperativeHandle(ref, () => ({
      open: () => {
        initialRangeRef.current = selectedRange;
        setLocalDateRange(selectedRange);
        setOpen(true);
      },
    }));

    useEffect(() => {
      if (!isOpen) {
        initialRangeRef.current = selectedRange;
        setLocalDateRange(selectedRange);
      }
    }, [isOpen, selectedRange]);

    const handleClose = React.useCallback(() => {
      rest.onClose?.();
      setOpen(false);
      setLocalDateRange(initialRangeRef.current);
    }, []);

    const handleSubmit = React.useCallback(() => {
      if (localDateRange) {
        onRangeSubmit(localDateRange);
      }
      setOpen(false);
    }, [localDateRange, onRangeSubmit]);

    const handleDateChange = React.useCallback((newDateRange: DateRange) => {
      setLocalDateRange(newDateRange);
    }, []);

    const modalContent = useMemo(
      () => (
        <StyledDateRangePicker
          selectedRange={localDateRange}
          maxDaysCount={rest.maxDaysCount}
          singleDateSelection={singleDateSelection}
          minDate={rest.minDate}
          maximumDate={rest.maximumDate}
          rollingMaxDays={rest.rollingMaxDays}
          onChange={handleDateChange}
          setIsWrongRange={setDisabledSubmitButton}
        />
      ),
      [
        localDateRange?.start,
        localDateRange?.end,
        singleDateSelection,
        rest.maxDaysCount,
        rest.minDate,
        rest.maximumDate,
        rest.rollingMaxDays,
        handleDateChange,
      ],
    );

    return (
      <Modal
        open={isOpen}
        title={rest.modalTitle || rest.t('common.filterBy')}
        width="54rem"
        submitButtonTitle={rest.t('common.apply')}
        cancelButtonTitle={rest.t('components.dateRangePicker.clearFilter')}
        onClose={handleClose}
        onCancel={rest.onReset}
        onSubmit={handleSubmit}
        disabledSubmitButton={disabledSubmitButton}
      >
        {modalContent}
      </Modal>
    );
  }),
);

const DateFilterButton = React.memo(
  ({
    modalTitle,
    dateRange,
    maximumDate,
    minDate,
    maxDaysCount,
    singleDateSelection,
    breakpointShowButtonText,
    onRangeSelect,
    onReset,
    disabled,
    placeholder,
    rollingMaxDays,
    ...rest
  }: DateFilterButtonProps) => {
    const { t } = useTranslation();
    const [modalKey, setModalKey] = useState(Math.random());
    const dateRangeFilterRef = React.useRef(null);

    const selectedRangeText = React.useMemo(() => {
      if (dateRange && dateRange.start && dateRange.end) {
        if (singleDateSelection) {
          // For single date selection, show only the start date
          return t('dateShort', { val: dateRange.start });
        } else {
          // For range selection, show the full range
          return `${t('dateShort', { val: dateRange.start })} - ${t('dateShort', { val: dateRange.end })}`;
        }
      }
      return placeholder || t('components.dateRangePicker.selectRange');
    }, [dateRange?.start, dateRange?.end, singleDateSelection, placeholder, t]);

    const handleButtonClick = React.useCallback(() => {
      dateRangeFilterRef.current?.open();
    }, []);

    return (
      <>
        <FilterButton onClick={handleButtonClick} {...rest} disabled={disabled}>
          <CalendarIcon />
          <ButtonText breakpoint={breakpointShowButtonText}>{selectedRangeText}</ButtonText>
          {dateRange && <StyledBadge breakpoint={breakpointShowButtonText}>1</StyledBadge>}
        </FilterButton>
        <DateRangeModal
          key={modalKey}
          ref={dateRangeFilterRef}
          selectedRange={dateRange}
          onRangeSubmit={onRangeSelect}
          modalTitle={modalTitle}
          maximumDate={maximumDate}
          minDate={minDate}
          maxDaysCount={maxDaysCount}
          rollingMaxDays={rollingMaxDays}
          onClose={() => setModalKey(Math.random())}
          singleDateSelection={singleDateSelection}
          onReset={onReset}
          t={t}
        />
      </>
    );
  },
);

export default DateFilterButton;
